/**
 * Click Validation Hook
 * 
 * Intercepts clicks on gameplay actions and validates stale data before allowing
 * the action to proceed. Excludes settings and navigation buttons via data-no-sync attribute.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { AxialCoord } from '../types/terrain';
import { CombatState, PendingEncounter, Player } from '../types/player';
import { ensurePlayerReady } from '../utils/dailyReset';
import { createClientStateSnapshot, GameplayState } from '../utils/stateSync';

interface UseClickValidationProps {
  player: Player;
  playerPosition: AxialCoord;
  combatState: CombatState | null;
  pendingEncounter: PendingEncounter | null;
  activeQuestId?: string | null;
  questState?: {
    currentSectionIndex: number;
    currentChoices?: any[];
    questVariables?: Record<string, any>;
  } | null;
  travelState?: {
    route: AxialCoord[];
    currentRouteIndex: number;
  } | null;
  updatePosition: (position: AxialCoord) => void;
  updatePlayer: (updates: { hp: number; stamina: number; maxHp: number; maxStamina: number }) => void;
  markHexesExplored: (hexes: AxialCoord[]) => void;
  setCombatState: (state: CombatState | null) => void;
  setPendingEncounter: (state: PendingEncounter | null) => void;
  setActiveQuestId?: (id: string | null) => void;
  setQuestState?: (state: GameplayState['questState']) => void;
  setActionCompletionMessage: (message: string | null) => void;
  containerRef: React.RefObject<HTMLElement | null>;
}

/**
 * Check if an element or any of its parents has the data-no-sync attribute
 */
function shouldExcludeClick(element: HTMLElement | null): boolean {
  if (!element) return false;
  
  let current: HTMLElement | null = element;
  while (current) {
    if (current.getAttribute('data-no-sync') === 'true') {
      return true;
    }
    current = current.parentElement;
  }
  
  return false;
}

/**
 * Check if click is on the mini map (which has its own validation)
 * Movement actions have their own validation in handleMiniMapHexClick
 */
function isMiniMapClick(element: HTMLElement | null): boolean {
  if (!element) return false;
  
  let current: HTMLElement | null = element;
  while (current) {
    // Check if element or parent has map-container class (MapContainer component)
    if (current.classList.contains('map-container')) {
      return true;
    }
    // Check for canvas elements that are likely map canvases
    if (current.tagName === 'CANVAS') {
      // Check parent chain for map-container
      let parent = current.parentElement;
      while (parent) {
        if (parent.classList.contains('map-container')) {
          return true;
        }
        parent = parent.parentElement;
      }
    }
    current = current.parentElement;
  }
  
  return false;
}

export function useClickValidation({
  player,
  playerPosition,
  combatState,
  pendingEncounter,
  activeQuestId,
  questState,
  travelState,
  updatePosition,
  updatePlayer,
  markHexesExplored,
  setCombatState,
  setPendingEncounter,
  setActiveQuestId,
  setQuestState,
  setActionCompletionMessage,
  containerRef,
}: UseClickValidationProps): { isValidating: boolean } {
  const isValidatingRef = useRef(false);
  const [isValidating, setIsValidating] = useState(false);

  const validateAndProceed = useCallback(async (event: MouseEvent) => {
    // Prevent multiple simultaneous validations
    if (isValidatingRef.current) {
      return;
    }

    isValidatingRef.current = true;
    setIsValidating(true);

    try {
      // Create client state snapshot
      const clientState = createClientStateSnapshot(
        player,
        playerPosition,
        combatState,
        pendingEncounter,
        activeQuestId,
        questState,
        travelState
      );
      
      // Call backend validation
      const resetResult = await ensurePlayerReady(clientState);

      // Check if server detected state mismatch
      if (resetResult.syncRequired && resetResult.gameplayState) {
        // Reload page to get fresh state from server
        // This is simpler than applying state manually and ensures complete sync
        isValidatingRef.current = false;
        setIsValidating(false);
        window.location.reload();
        return;
      }

      // No sync required - allow click to proceed
      // Re-dispatch the click event on the original target SYNCHRONOUSLY
      // This ensures validation completes BEFORE the action executes
      const target = event.target as HTMLElement;
      if (target && target.isConnected) {
        // Create a new click event and dispatch it
        const newEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: event.detail,
          button: event.button,
          buttons: event.buttons,
          clientX: event.clientX,
          clientY: event.clientY,
        });
        
        // Mark this event as already validated to prevent re-interception
        (newEvent as any).__validatedByInterceptor = true;
        
        // Dispatch synchronously - no setTimeout
        // This blocks until the event handler completes
        target.dispatchEvent(newEvent);
      }
    } catch (error) {
      // On error, allow click to proceed (fail open)
      const target = event.target as HTMLElement;
      if (target && target.isConnected) {
        const newEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        // Mark this event as already validated to prevent re-interception
        (newEvent as any).__validatedByInterceptor = true;
        // Dispatch synchronously - no setTimeout
        target.dispatchEvent(newEvent);
      }
    } finally {
      isValidatingRef.current = false;
      setIsValidating(false);
    }
  }, [
    player,
    playerPosition,
    combatState,
    pendingEncounter,
    activeQuestId,
    questState,
    travelState,
    updatePosition,
    updatePlayer,
    markHexesExplored,
    setCombatState,
    setPendingEncounter,
    setActiveQuestId,
    setQuestState,
    setActionCompletionMessage,
  ]);

  const handleClick = useCallback((event: MouseEvent) => {
    // Skip events that were re-dispatched by us (to prevent infinite loop)
    if ((event as any).__validatedByInterceptor) {
      return;
    }

    // Only intercept clicks within the game container
    if (!containerRef.current || !containerRef.current.contains(event.target as Node)) {
      return;
    }

    const target = event.target as HTMLElement;
    
    // Check if click should be excluded
    if (shouldExcludeClick(target)) {
      return; // Allow click to proceed normally
    }

    // Check if click is on mini map (which has its own validation)
    if (isMiniMapClick(target)) {
      return; // Allow click to proceed normally
    }

    // Intercept the click for validation
    event.preventDefault();
    event.stopPropagation();
    
    // Validate and proceed
    validateAndProceed(event);
  }, [containerRef, validateAndProceed]);

  useEffect(() => {
    // Attach click listener to document
    document.addEventListener('click', handleClick, true); // Use capture phase

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [handleClick]);

  return { isValidating };
}

